import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
});

// Attach token to every request
API.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('itsm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      sessionStorage.removeItem('itsm_token');
      sessionStorage.removeItem('itsm_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ===== AUTH =====
export const login = (data) => API.post('/auth/login', data);
export const microsoftLogin = (accessToken) => API.post('/auth/microsoft', { accessToken });
export const getMe = () => API.get('/auth/me');
export const registerUser = (data) => API.post('/auth/register', data);
export const ensureRequestor = (data) => API.post('/auth/ensure-requestor', data);
export const getAllUsers = () => API.get('/auth/users');
export const getConsultants = () => API.get('/auth/consultants');
export const getRequesters = () => API.get('/auth/requesters');
export const getGroups = () => API.get('/groups');
export const createGroup = (data) => API.post('/groups', data);
export const deleteGroup = (id) => API.delete(`/groups/${id}`);
export const toggleUserStatus    = (id)       => API.patch(`/auth/users/${id}/toggle`);
export const editUser            = (id, data) => API.patch(`/auth/users/${id}/edit`, data);
export const changeUserPassword  = (id, data) => API.patch(`/auth/users/${id}/password`, data);
export const deleteUser          = (id)       => API.delete(`/auth/users/${id}`);
export const updateOnlineStatus  = (isOnline) => API.patch('/auth/me/online', { is_online: isOnline });
export const sendHeartbeat       = ()         => API.post('/auth/me/heartbeat');

// Fire-and-forget "go offline" that survives the page being closed/unloaded.
// Uses fetch keepalive (sendBeacon can't attach the Authorization header).
export const markOfflineBeacon = () => {
  const token = sessionStorage.getItem('itsm_token');
  if (!token) return;
  const base = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  try {
    fetch(`${base}/auth/me/online`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ is_online: false }),
      keepalive: true,
    });
  } catch (_) { /* ignore — page is going away */ }
};
export const updateMyProfile     = (data)     => API.patch('/auth/me', data);
export const changeMyPassword    = (data)     => API.patch('/auth/me/password', data);

// ===== TICKETS =====
export const getTickets = (params) => API.get('/tickets', { params });
export const getTicketById = (id) => API.get(`/tickets/${id}`);
export const createTicket = (data) => API.post('/tickets', data);
export const updateTicket = (id, data) => API.patch(`/tickets/${id}`, data);
export const addComment = (id, data) => API.post(`/tickets/${id}/comments`, data);
export const logTime = (id, data) => API.post(`/tickets/${id}/time-log`, data);
export const getTicketStats      = ()       => API.get('/tickets/stats');
export const getTicketCompanies  = ()       => API.get('/tickets/companies');
// Recycle Bin (admin only) — delete is a soft delete, so it is reversible.
export const softDeleteTicket    = (id)     => API.delete(`/tickets/${id}`);
export const restoreTicket       = (id)     => API.post(`/tickets/${id}/restore`);
export const getDeletedTickets   = (params) => API.get('/tickets/deleted', { params });
export const bulkUpdateTickets   = (data)   => API.post('/tickets/bulk-update', data);
export const exportTickets       = (params) => API.get('/tickets/export', { params, responseType: 'blob' });

// ===== PROJECTS =====
export const getProjects      = ()         => API.get('/projects');
export const createProject    = (data)     => API.post('/projects', data);
export const updateProject    = (id, data) => API.patch(`/projects/${id}`, data);
export const deleteProject    = (id)       => API.delete(`/projects/${id}`);
export const getProjectMembers = (id)      => API.get(`/projects/${id}/members`);
export const addProjectMember  = (id, data)=> API.post(`/projects/${id}/members`, data);

// ===== SUB-PROJECTS =====
export const getSubProjects    = (params) => API.get('/sub-projects', { params });
export const getSubProjectById = (id)     => API.get(`/sub-projects/${id}`);
export const createSubProject  = (data)   => API.post('/sub-projects', data);
export const updateSubProject  = (id, d)  => API.patch(`/sub-projects/${id}`, d);
export const deleteSubProject  = (id)     => API.delete(`/sub-projects/${id}`);

// ===== NOTIFICATIONS =====
export const getNotifications = () => API.get('/notifications');
export const markNotificationRead = (id) => API.patch(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => API.patch('/notifications/read-all');

// ===== KNOWLEDGE BASE =====
export const getKBArticles = (params) => API.get('/kb/articles', { params });
export const getKBArticleById = (id) => API.get(`/kb/articles/${id}`);
export const createKBArticle = (data) => API.post('/kb/articles', data);
export const updateKBArticle = (id, data) => API.patch(`/kb/articles/${id}`, data);
export const deleteKBArticle = (id) => API.delete(`/kb/articles/${id}`);

// ===== REPORTS =====
export const getReportSummary     = (params) => API.get('/reports/summary',    { params });
export const getReportTrends      = (params) => API.get('/reports/trends',     { params });
export const getConsultantStats   = ()        => API.get('/reports/consultants');

// ===== ESCALATION & FEEDBACK =====
export const escalateTicket   = (id, data) => API.post(`/tickets/${id}/escalate`, data);
export const clearEscalation  = (id)       => API.post(`/tickets/${id}/escalate/clear`);
export const getSubTickets   = (id)       => API.get(`/tickets/${id}/sub-tickets`);
export const createSubTicket = (id, data) => API.post(`/tickets/${id}/sub-tickets`, data);
export const submitFeedback  = (id, data) => API.post(`/tickets/${id}/feedback`, data);
export const acceptTicket    = (id)       => API.post(`/tickets/${id}/accept`, {});
export const resolveWithKB   = (id, data) => API.post(`/tickets/${id}/resolve-with-kb`, data);

// ===== SLA =====
export const getSLAStatus     = (id)          => API.get(`/tickets/${id}/sla`);
export const getSLADashboard  = ()            => API.get('/sla/dashboard');
export const pauseSLA         = (id, data)    => API.post(`/tickets/${id}/sla/pause`, data);
export const resumeSLA        = (id)          => API.post(`/tickets/${id}/sla/resume`, {});

// ===== LEAVES =====
export const getMyLeaves    = ()      => API.get('/leaves/my');
export const getAllLeaves    = (p)     => API.get('/leaves', { params: p });
export const applyLeave     = (data)  => API.post('/leaves', data);
export const approveLeave   = (id)    => API.patch(`/leaves/${id}/approve`);
export const rejectLeave    = (id)    => API.patch(`/leaves/${id}/reject`);
export const cancelLeave    = (id)    => API.delete(`/leaves/${id}`);

// ===== ATTACHMENTS =====
export const uploadFile = (formData) =>
  API.post('/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getAttachments = (id) => API.get(`/tickets/${id}/attachments`);
export const uploadAttachment = (id, formData) =>
  API.post(`/tickets/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteAttachment = (ticketId, attachmentId) =>
  API.delete(`/tickets/${ticketId}/attachments/${attachmentId}`);

// ===== AUDIT =====
export const getAuditLog = (params) => API.get('/audit', { params });

// ===== CHECK-IN / CHECK-OUT =====
export const checkIn        = (data)     => API.post('/checkin', data);
export const checkOut       = (id, data) => API.post(`/checkin/${id}/checkout`, data);
export const getMyCheckIns  = ()         => API.get('/checkin/my');
export const getCheckInStatus = ()       => API.get('/checkin/status');
export const getAllCheckIns  = (params)  => API.get('/checkin/all', { params });

// ===== SETTINGS =====
export const getSettings    = ()     => API.get('/settings');
export const updateSettings = (data) => API.patch('/settings', data);

// ===== HOLIDAYS =====
export const getHolidays    = (params) => API.get('/holidays', { params });
export const createHoliday  = (data)   => API.post('/holidays', data);
export const deleteHoliday  = (id)     => API.delete(`/holidays/${id}`);

// ===== ESCALATION RULES =====
export const getEscalationRules   = ()       => API.get('/escalation-rules');
export const getEscalationBreaches= ()       => API.get('/escalation-rules/breaches');
export const createEscalationRule = (data)   => API.post('/escalation-rules', data);
export const updateEscalationRule = (id, d)  => API.patch(`/escalation-rules/${id}`, d);
export const deleteEscalationRule = (id)     => API.delete(`/escalation-rules/${id}`);

// ===== PRODUCTIVITY =====
export const getProductivity = (params) => API.get('/reports/productivity', { params });

// ===== AGENTIC AI (Phase 1: Auto-Triage) =====
export const aiHealth         = ()          => API.get('/ai/health');
export const aiAnalyze        = (id)        => API.post(`/ai/tickets/${id}/analyze`, {});
export const aiRegenerate     = (id)        => API.post(`/ai/tickets/${id}/regenerate`, {});
export const aiGetSuggestions = (id)        => API.get(`/ai/tickets/${id}/suggestions`);
export const aiApprove        = (sid, data) => API.post(`/ai/suggestions/${sid}/approve`, data || {});
export const aiReject         = (sid, data) => API.post(`/ai/suggestions/${sid}/reject`, data || {});
export const aiRespond        = (id)        => API.post(`/ai/tickets/${id}/respond`, {});
export const aiSummarize      = (id)        => API.post(`/ai/tickets/${id}/summarize`, {});
export const aiKbAsk          = (question)  => API.post('/ai/kb/ask', { question });

export default API;
